import { motion } from "framer-motion";
import { Quote, Target, Compass, HeartPulse, Sparkles, Lightbulb, Users } from "lucide-react";
import { useSettings, assetUrl } from "@/context/SettingsContext";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

const MISSION_ICONS = [Sparkles, Compass, HeartPulse, Lightbulb, Users];

export default function About() {
  const { settings } = useSettings();
  const about = settings?.about || {};
  const brand = settings?.branding || {};

  return (
    <div data-testid="about-page">
      <section className="relative py-20 lg:py-28 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 items-start">
          <motion.div {...fadeUp} className="lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">{about.eyebrow || "About Us"}</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-[1.05] tracking-tight">
              {about.heading}
            </h1>
            <p className="text-slate-600 text-lg mt-6 leading-relaxed">{about.paragraph1}</p>
            <p className="text-slate-600 mt-4 leading-relaxed">{about.paragraph2}</p>
          </motion.div>
          {about.image && (
            <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.12 }} className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(15,23,42,0.25)]">
                <img src={assetUrl(about.image)} alt="Ambigara students" className="w-full h-[420px] object-cover" />
              </div>
            </motion.div>
          )}
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-6 lg:gap-10">
          <motion.div {...fadeUp} data-testid="vision-card" style={{ backgroundColor: "var(--brand-primary)" }} className="text-white rounded-3xl p-10 shadow-[0_30px_60px_-20px_rgba(30,58,138,0.4)]">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-11 h-11 rounded-xl bg-white/10 grid place-items-center">
                <Target className="w-5 h-5" />
              </span>
              <p className="text-xs font-bold uppercase tracking-[0.24em]" style={{ color: "var(--brand-secondary)" }}>Our Vision</p>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl leading-snug">{about.vision}</h2>
          </motion.div>

          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }} data-testid="mission-card" className="bg-white rounded-3xl p-10 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-11 h-11 rounded-xl grid place-items-center" style={{ backgroundColor: "rgba(244,63,94,0.08)", color: "var(--brand-secondary)" }}>
                <Compass className="w-5 h-5" />
              </span>
              <p className="text-xs font-bold uppercase tracking-[0.24em] brand-secondary-text">Our Mission</p>
            </div>
            <ul className="space-y-4">
              {(about.mission || []).map((m, i) => {
                const Icon = MISSION_ICONS[i % MISSION_ICONS.length];
                return (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-lg grid place-items-center shrink-0 mt-0.5" style={{ backgroundColor: "rgba(30,58,138,0.08)", color: "var(--brand-primary)" }}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="text-slate-700 leading-relaxed">{m}</span>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">Leadership</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">Messages from our Leaders</h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {[about.chairman, about.principal].filter(Boolean).map((l, i) => (
              <motion.article
                key={l.name}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                data-testid={`leader-${(l.title || "").toLowerCase()}`}
                className="bg-white rounded-3xl p-8 sm:p-10 border border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] relative overflow-hidden"
              >
                <Quote className="absolute top-6 right-6 w-14 h-14" style={{ color: "rgba(30,58,138,0.10)" }} />
                <p className="text-xs font-bold uppercase tracking-[0.2em] brand-secondary-text mb-3">{l.title}&rsquo;s Message</p>
                <p className="text-slate-700 leading-relaxed text-[15px]">&ldquo;{l.message}&rdquo;</p>
                <div className="mt-8 pt-6 border-t border-slate-100 flex items-center gap-4">
                  {l.photo ? (
                    <img src={assetUrl(l.photo)} alt={l.name} className="w-14 h-14 rounded-full object-cover" />
                  ) : (
                    <div
                      className="w-14 h-14 rounded-full grid place-items-center font-heading text-xl text-white"
                      style={{ backgroundColor: i === 0 ? "var(--brand-primary)" : "var(--brand-secondary)" }}
                    >
                      {l.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}
                    </div>
                  )}
                  <div>
                    <p className="font-heading text-lg text-slate-900">{l.name}</p>
                    <p className="text-sm text-slate-500">{l.title}, {brand.collegeName}</p>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
